import clsx from 'clsx';
import { Button } from '../../typography/button/button';
import { FieldsType, Labels } from './types';
import { Product } from '@/queries/kyc-survey';
import { Loading } from '@/isomorphic/components/loading/loading';
import { useLocalization } from '@/hooks/use-localization';
import { useGtm } from '@/hooks/use-gtm';
import { AppContextType } from '@/contexts/app-context';
import { Arrow } from './components/svg';

type Props = {
    handleChange: (value: string | boolean, name: keyof FieldsType) => void;
    isLoading: boolean;
    labels: Labels;
    login: () => void;
    fetchedProducts?: Product[];
    values: FieldsType;
    step: number;
    user?: AppContextType['user'];
    setStep: (step: number) => void;
};

export const StepZero = ({ handleChange, isLoading, labels, login, fetchedProducts, values, step, user, setStep }: Props) => {
    const { getLocale } = useLocalization();
    const { sendPageInteraction } = useGtm();
    const { productCode } = values;

    const handleProductChoice = (code: string) => {
        sendPageInteraction({ action: 'Open savings account', label: 'Product Select', productCode: code });
        handleChange(code, 'productCode');
    };

    function renderInterestRate(product: Product) {
        const interestType = product.interest.type === 'VARIABLE' ? labels?.variableInterestRate || 'rörlig' : labels?.fixedInterestRate || 'fast';
        const interestRate = Intl.NumberFormat(getLocale(), { minimumFractionDigits: 2 }).format(product.interest.rate);

        return (
            <>
                {interestRate}% {interestType} {labels?.interestRate || 'ränta'}
            </>
        );
    }

    return (
        <>
            <div className="r-space-y-4">
                <h4 className="r-text-xl r-font-semibold">{labels.formStepZeroHeading || 'Välj sparkontot som passar dina behov'}</h4>
                <div
                    className="styled-link"
                    dangerouslySetInnerHTML={{
                        __html: labels?.formStepZeroDescription || '',
                    }}
                />

                {isLoading && (
                    <div className="r-flex r-min-h-24 r-flex-col r-items-center r-justify-center" role="status">
                        <Loading />
                    </div>
                )}

                {fetchedProducts && fetchedProducts.length > 0 && (
                    <div className="r-flex r-flex-col r-gap-4">
                        {fetchedProducts.map(product => (
                            <button
                                key={product.code}
                                className={clsx(
                                    'r-flex r-flex-col r-gap-2 r-rounded-xl r-border r-border-primary-300 r-p-4 r-ring r-transition-all r-duration-100 md:hover:r-ring-[2px] md:hover:r-ring-primary-300',
                                    {
                                        'r-ring-[2px] r-ring-primary-300': product.code === productCode,
                                        'r-ring-transparent': product.code !== productCode,
                                    }
                                )}
                                onClick={() => handleProductChoice(product.code)}
                            >
                                <div className="r-flex r-w-full r-flex-row r-justify-between">
                                    <h5 className="r-text-xl r-font-semibold">{product.name}</h5>
                                    <div className="r-flex r-items-center r-rounded-md r-bg-[#bfdfde] r-p-2 r-text-xs r-font-light r-text-primary-300">
                                        <p className="r-mb-0">{renderInterestRate(product)}</p>
                                    </div>
                                </div>
                                <p className="r-text-left">{product.description}</p>
                            </button>
                        ))}
                    </div>
                )}

                {user?.isAuthenticated ? (
                    <Button onClick={() => setStep(2)} fullWidth type="tertiary">
                        <div className="r-flex r-items-center r-gap-2">
                            <p>
                                {labels.continue} {step + 1}
                            </p>
                            <Arrow className="r-w-4" />
                        </div>
                    </Button>
                ) : (
                    <Button disabled={!productCode} type="primary" fullWidth onClick={() => login()}>
                        <div className="r-flex r-space-x-1">
                            <p> {labels.formLoginButtonText}</p>
                            <img src="/assets/icons/bankid-se.svg" alt="bankid-icon" />
                        </div>
                    </Button>
                )}
            </div>
        </>
    );
};
